import { useState, useEffect } from 'react';
import { templates } from '../data/templates';
import { useAssistants } from '../context/AssistantContext';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import { KnowledgeBasePicker } from './KnowledgeBasePicker';

interface Props {
  onClose: () => void;
  preselectedTemplateId?: string | null;
}

type Step = 1 | 2 | 3;

export function CreateAssistantWizard({ onClose, preselectedTemplateId }: Props) {
  const { createAssistant, fetchAssistants } = useAssistants();
  const [step, setStep] = useState<Step>(1);
  const [hasChosenTemplateStep, setHasChosenTemplateStep] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [selectedKBIds, setSelectedKBIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-select preselected template on mount and skip to Step 2
  useEffect(() => {
    if (preselectedTemplateId) {
      const tmpl = templates.find(t => t.id === preselectedTemplateId);
      if (tmpl) {
        setHasChosenTemplateStep(true);
        setSelectedTemplateId(tmpl.id);
        setName(tmpl.name);
        setInstructions(tmpl.instructions);
        setStep(2);
      }
    }
  }, [preselectedTemplateId]);

  const selectedTemplate = selectedTemplateId
    ? templates.find(t => t.id === selectedTemplateId)
    : null;

  const selectTemplate = (templateId: string | null) => {
    setHasChosenTemplateStep(true);
    setSelectedTemplateId(templateId);
    if (templateId) {
      const tmpl = templates.find(t => t.id === templateId);
      if (tmpl) {
        setName(tmpl.name);
        setInstructions(tmpl.instructions);
      }
    } else {
      setName('');
      setInstructions('');
    }
  };

  const goToStep2 = () => {
    if (!hasChosenTemplateStep) {
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleFinish = async () => {
    if (!name.trim() || !instructions.trim()) {
      setError('Please enter a name and instructions.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await createAssistant(
        name.trim(),
        instructions.trim(),
        selectedTemplateId || undefined,
        selectedKBIds.length > 0 ? selectedKBIds : undefined
      );
      await fetchAssistants();
      onClose();
    } catch (err) {
      setError(
        formatErrorMessage(err instanceof Error ? err.message : 'Failed to create assistant.')
      );
    } finally {
      setCreating(false);
    }
  };

  const stepLabels: Record<Step, string> = {
    1: 'Choose Template',
    2: 'Link Knowledge Bases',
    3: 'Review & Create',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-card shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col min-h-0"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Create new assistant"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-800 font-heading">Create Assistant</h2>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            {([1, 2, 3] as Step[]).map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  s === step ? 'bg-primary-600 text-white' : s < step ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-400'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-primary-300' : 'bg-neutral-200'}`} />}
              </div>
            ))}
            <span className="text-xs text-neutral-500 ml-2">{stepLabels[step]}</span>
          </div>
        </div>

        {/* Body — scrolls; header + footer stay fixed in the popup */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {/* Step 1: Template selection */}
          {step === 1 && (
            <div className="space-y-3">
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => selectTemplate(t.id)}
                  className={`w-full text-left p-3 rounded-btn border transition-colors ${
                    selectedTemplateId === t.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <div className="text-sm font-medium text-neutral-800">{t.name}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{t.description}</div>
                </button>
              ))}
              <button
                onClick={() => selectTemplate(null)}
                className={`w-full text-left p-3 rounded-btn border border-dashed transition-colors ${
                  hasChosenTemplateStep && selectedTemplateId === null
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
              >
                <div className="text-sm font-medium text-neutral-500">Start From Scratch</div>
                <div className="text-xs text-neutral-400 mt-0.5">Write your own instructions.</div>
              </button>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
          )}

          {/* Step 2: Link Knowledge Bases */}
          {step === 2 && (
            <div>
              {selectedTemplate && (
                <p className="text-xs text-neutral-400 mb-3">
                  <span className="font-medium text-neutral-500">Suggested Files:</span>{' '}
                  {selectedTemplate.suggestedFileTypes}
                </p>
              )}
              <KnowledgeBasePicker
                selectedIds={selectedKBIds}
                onSelectionChange={setSelectedKBIds}
              />
              {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="My Assistant"
                  className="input w-full bg-neutral-50 border border-neutral-200 rounded-btn px-3 py-2 text-sm focus:border-primary-400 focus:bg-white focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Instructions</label>
                <textarea
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  placeholder="Describe what this assistant should do and how it should behave..."
                  rows={4}
                  className="input w-full resize-none bg-neutral-50 border border-neutral-200 rounded-btn px-3 py-2 text-sm focus:border-primary-400 focus:bg-white focus:outline-none transition-colors"
                />
              </div>
              <div className="text-xs text-neutral-400">
                {selectedKBIds.length}{' '}
                {selectedKBIds.length === 1 ? 'Knowledge Base Linked' : 'Knowledge Bases Linked'}
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-100 flex justify-between bg-white rounded-b-card">
          <button
            onClick={() => {
              if (step === 1) onClose();
              else if (step === 2) setStep(1);
              else setStep(2);
            }}
            className="btn-secondary text-sm"
            disabled={creating}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step === 1 && (
            <button
              onClick={goToStep2}
              disabled={!hasChosenTemplateStep || creating}
              className="btn-primary text-sm disabled:opacity-50 disabled:pointer-events-none"
            >
              Next
            </button>
          )}

          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              className="btn-primary text-sm"
            >
              Next
            </button>
          )}

          {step === 3 && (
            <button
              onClick={handleFinish}
              disabled={creating || !name.trim() || !instructions.trim()}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
